import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormItemModel, FormStateModel, GroupValueType, isSingleStepForm, StepItemModel } from '@libs/form/models';
import { ScrollService } from '@libs/shared/services';
import { debounceTime, Subscription } from 'rxjs';
// TODO: optimize import after resolving circular dependencies
import { ConditionalFieldsDirective, DynamicElementsDirective } from '../../../directives';
import { DYNAMIC_ELEMENTS_MAP, GroupBaseComponent } from '../../../models';
import { FormConfigService, FormStateQuery, FormStateStore } from '../../../services';
import { SingleItemsMap } from '../../single-items-map';
import { StepComponent } from '../step';

@Component({
  selector: 'ui-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  imports: [
    DynamicElementsDirective,
    ConditionalFieldsDirective,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    FormConfigService,
    {
      provide: DYNAMIC_ELEMENTS_MAP,
      useValue: {
        ...SingleItemsMap,
        step: StepComponent,
      },
    },
  ],
})
export class FormComponent extends GroupBaseComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private readonly useStore: boolean = false;
  private stepValueSubscription?: Subscription;
  private previousFormValue: GroupValueType = {};

  private readonly store: FormStateStore = inject(FormStateStore);
  private readonly query: FormStateQuery = inject(FormStateQuery);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly formConfigService: FormConfigService = inject(FormConfigService);
  private readonly scrollService: ScrollService = inject(ScrollService);

  @Input() public data!: FormItemModel;
  @Input() public scrollToTop: boolean = false;
  @Input() public values?: GroupValueType;
  @Output() public pageSubmit: EventEmitter<GroupValueType> = new EventEmitter<GroupValueType>();
  @Output() public valueChange: EventEmitter<GroupValueType> = new EventEmitter<GroupValueType>();
  public override control: FormGroup = new FormGroup({});
  public currentStepNumber!: number;

  public get currentStepControl(): FormGroup {
    return this.control.controls[this.currentStepGroupModel.name] as FormGroup;
  }

  public get currentStepGroupModel(): StepItemModel {
    return isSingleStepForm(this.data) ? this.data : this.data.items[this.currentStepNumber];
  }

  public ngOnInit(): void {
    this.updateFormConfigService();

    this.currentStepNumber = 0;

    if (isSingleStepForm(this.data)) {
      return;
    }

    this.data.items.forEach((item: StepItemModel, index: number) => {
      item.attributes = {
        ...item.attributes,
        hidden: index !== this.currentStepNumber,
      };
    });
  }

  public ngAfterViewInit(): void {
    this.initializeState();

    this.changeDetectorRef.detectChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.updateFormConfigService();

      this.stepValueSubscription?.unsubscribe();
      this.stepValueSubscription = new Subscription();

      this.stepValueSubscription.add(
        this.control.valueChanges.subscribe(
          this.updateStateStorage.bind(this),
        ),
      );

      this.stepValueSubscription.add(
        this.control.valueChanges
          .pipe(debounceTime(100))
          .subscribe((formValue: GroupValueType) => {
            const changedValue: GroupValueType = Object.keys(formValue).reduce((acc, key) => {
              if (formValue[key] !== this.previousFormValue[key]) {
                acc[key] = formValue[key];
              }

              return acc;
            }, {} as GroupValueType);

            this.valueChange.emit(changedValue);
            this.previousFormValue = { ...formValue };
          }),
      );
    }

    if (changes['values']?.currentValue && !changes['values'].firstChange) {
      this.initializeState();
    }
  }

  public ngOnDestroy(): void {
    this.stepValueSubscription?.unsubscribe();
  }

  public onSubmit(event: SubmitEvent): void {
    const submitter: string = (event.submitter as HTMLButtonElement).name;

    if (this.scrollToTop) {
      this.scrollService.contentScrollToTop();
    }

    this.updateStateStorage();

    if (submitter === 'prev' && this.currentStepNumber > 0) {
      this.currentStepControl.markAsUntouched();
      this.nextStep(-1);
    } else if (submitter === 'next' || submitter === 'save') {
      this.currentStepControl.markAllAsTouched();
      this.pageSubmit.emit(this.currentStepControl.value as GroupValueType);

      if (this.currentStepControl.valid && this.currentStepNumber < this.data.items.length - 1) {
        this.nextStep(+1);
      }
    }
  }

  private nextStep(next: 1 | -1): void {
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    this.currentStepGroupModel.attributes!.hidden = true;
    this.currentStepNumber += next;
    this.currentStepGroupModel.attributes!.hidden = false;
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
  }

  /* istanbul ignore next */
  private initializeState(): void {
    if (this.values) {
      this.control.setValue(this.values);

      return;
    }

    if (!this.useStore) {
      return;
    }

    // We load the stored form date from the session storage!
    const state: FormStateModel = this.query.getValue();

    if (Object.keys(state.value).length === 0) {
      this.store.update({
        currentStep: this.currentStepNumber,
        value: this.control.value as GroupValueType,
      });
    } else {
      this.currentStepNumber = state.currentStep;
      this.control.setValue(state.value);
    }
  }

  /* istanbul ignore next */
  private updateStateStorage(): void {
    if (!this.useStore) {
      return;
    }

    this.store.update({
      currentStep: this.currentStepNumber,
      value: this.control.value as GroupValueType,
    });
  }

  private updateFormConfigService(): void {
    if (!isSingleStepForm(this.data)) {
      this.formConfigService.setConfig({
        form: this.data,
        control: this.control,
      });
    }
  }
}
